import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { Step } from '@atlaskit/editor-prosemirror/transform-override';

import { createStepsForNode } from './create-steps-for-node';
import { mergeSteps } from './merge-steps';
import type { DiffContext } from './types';
import { nodeEqIgnoring } from './utils';

/**
 * Options for {@link diffNodes}.
 */
export type DiffOptions = {
	/**
	 * Mark types to treat as invisible to the diff. Marks of these types are
	 * never compared, never emitted as Add/RemoveMark steps, and never cause a
	 * Match to fail.
	 *
	 * Use this for marks that aren't representable in the source format being
	 * diffed. Concrete case: when CM emits markdown back to PM, the parsed PM
	 * doc has no inline-comment marks (markdown can't encode them). Without
	 * ignoring them, every CM sync would emit RemoveMark steps for every comment
	 * in the document. With them ignored, PM's standard mark-mapping carries the
	 * comments through whatever text edits the diff produces.
	 */
	ignoreMarkTypes?: readonly string[];
	/**
	 * Upper bound on the number of LCS match pairs inside a single text-run
	 * diff before the algorithm bails out and emits one wholesale `ReplaceStep`
	 * for the differing range. Caps the worst case; bump it when a paragraph
	 * carries many small edits (typo fixes, spell-check passes) and you want
	 * surgical steps instead of a full-paragraph replace. Defaults to 10.
	 */
	maxTextRegions?: number;
};

const DEFAULT_MAX_TEXT_REGIONS = 10;

function makeContext(schema: Schema, opts?: DiffOptions): DiffContext {
	return {
		schema,
		ignoreMarks: new Set(opts?.ignoreMarkTypes ?? []),
		maxTextRegions: opts?.maxTextRegions ?? DEFAULT_MAX_TEXT_REGIONS,
	};
}

/**
 * Top-level entry. Diffs two PM documents and produces a list of Steps in
 * old-doc coordinates. The consumer is expected to apply them through a
 * `Transform` and map each subsequent step through the prior `tr.mapping`:
 *
 * ```ts
 * const tr = state.tr;
 * for (const step of steps) {
 *   const mapped = step.map(tr.mapping);
 *   if (mapped) tr.maybeStep(mapped);
 * }
 * ```
 *
 * Use `opts.ignoreMarkTypes` to make certain mark types invisible to the diff.
 * Typical case: ignore inline-comment / annotation marks when the diff source
 * is markdown (which can't represent them); PM's mark-mapping then carries
 * them through whatever text edits the diff produces.
 */
export function diffNodes(oldDoc: PMNode, newDoc: PMNode, opts?: DiffOptions): Step[] {
	if (oldDoc.type.schema !== newDoc.type.schema) {
		throw new Error('diffNodes: schemas differ between old and new doc');
	}
	const ctx = makeContext(oldDoc.type.schema, opts);
	if (nodeEqIgnoring(oldDoc, newDoc, ctx.ignoreMarks)) return [];
	return mergeSteps(createStepsForNode(oldDoc, newDoc, -1, ctx));
}
